"use client";

import React from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { CheckCircle, FileText, Settings, Users, Calendar, Target, AlertCircle, Clock } from "lucide-react";

export default function ModalTabbedExample() {
  const [open, setOpen] = React.useState(true);
  const [tab, setTab] = React.useState("overview");

  const projectGoals = [
    { title: "Complete Initial Setup", status: "completed", progress: 100 },
    { title: "Configure Team Access", status: "in-progress", progress: 75 },
    { title: "Import Existing Documents", status: "pending", progress: 0 },
    { title: "Establish Workflows", status: "pending", progress: 0 }
  ];

  const teamMembers = [
    { name: "Sarah Johnson", role: "Project Lead", status: "active" },
    { name: "Mike Chen", role: "Technical Lead", status: "active" },
    { name: "Emily Davis", role: "Compliance Officer", status: "pending" }
  ];

  return (
    <div className="space-y-4">
      <Button onClick={() => setOpen(true)}>Open Project Setup</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[60vw] h-screen bg-transparent shadow-none border-0 p-0 flex items-center justify-center" style={{ width: '60vw', maxWidth: '60vw' }}>
          <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-white/10 flex flex-col" style={{ width: '1280px', maxWidth: '1280px' }}>
            {/* Header */}
            <div className="p-6 pb-4 border-b border-white/10 bg-linear-to-b from-primary/5 to-transparent">
              <h2 className="text-3xl font-bold text-foreground">Project Setup Centre</h2>
              <p className="mt-2 text-muted-foreground">Configure your project settings, team, and workflows</p>
              
              <div className="mt-3 flex items-center gap-4 text-sm">
                <Badge variant="outline">Setup Phase</Badge>
                <Badge variant="secondary">25% Complete</Badge>
              </div>
            </div>

            {/* Tabs Content */}
            <div className="px-6 pt-4 flex-1 overflow-hidden flex flex-col">
              <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col overflow-hidden">
                <TabsList className="grid w-full grid-cols-3 bg-white/5 border border-white/10 shrink-0">
                  <TabsTrigger value="overview" className="flex items-center gap-2">
                    <Target className="h-4 w-4" />
                    Overview
                  </TabsTrigger>
                  <TabsTrigger value="details" className="flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    Details
                  </TabsTrigger>
                  <TabsTrigger value="notes" className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Notes
                  </TabsTrigger>
                </TabsList>
                
                <div className="flex-1 overflow-hidden">
                  <div className="h-full overflow-y-auto p-6">
                    {/* Overview Tab */}
                    <TabsContent value="overview" className="mt-0 h-full space-y-6">
                      <Card className="bg-white/5 border-white/20">
                        <CardHeader>
                          <CardTitle className="text-xl flex items-center gap-2">
                            <CheckCircle className="h-5 w-5 text-green-600" />
                            Project Goals
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {projectGoals.map((goal, index) => (
                            <div key={index} className="space-y-2">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  {goal.status === 'completed' ? (
                                    <CheckCircle className="h-4 w-4 text-green-600" />
                                  ) : goal.status === 'in-progress' ? (
                                    <AlertCircle className="h-4 w-4 text-orange-600" />
                                  ) : (
                                    <Clock className="h-4 w-4 text-muted-foreground" />
                                  )}
                                  <span className="text-sm font-medium">{goal.title}</span>
                                </div>
                                <Badge 
                                  variant={goal.status === 'completed' ? 'default' : goal.status === 'in-progress' ? 'secondary' : 'outline'}
                                  className="text-xs"
                                >
                                  {goal.status}
                                </Badge>
                              </div>
                              <Progress value={goal.progress} className="h-2 bg-white/10" />
                            </div>
                          ))}
                        </CardContent>
                      </Card>

                      <Card className="bg-white/5 border-white/20">
                        <CardHeader>
                          <CardTitle className="text-xl flex items-center gap-2">
                            <Users className="h-5 w-5" />
                            Team Overview
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {teamMembers.map((member, index) => (
                              <div key={index} className="flex items-center gap-3 p-3 rounded-lg border border-white/10 bg-white/5">
                                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                                  <span className="text-sm font-medium text-primary">
                                    {member.name.split(' ').map(n => n[0]).join('')}
                                  </span>
                                </div>
                                <div className="flex-1">
                                  <div className="font-medium text-sm">{member.name}</div>
                                  <div className="text-xs text-muted-foreground">{member.role}</div>
                                </div>
                                <Badge 
                                  variant={member.status === 'active' ? 'default' : 'outline'}
                                  className="text-xs"
                                >
                                  {member.status}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    </TabsContent>

                    {/* Details Tab */}
                    <TabsContent value="details" className="mt-0 h-full space-y-6">
                      <Card className="bg-white/5 border-white/20">
                        <CardHeader>
                          <CardTitle className="text-xl flex items-center gap-2">
                            <Settings className="h-5 w-5" />
                            Project Configuration
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-foreground mb-2">Project Name</label>
                              <Input placeholder="Enter project name" className="bg-white/10 border-white/20" />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-foreground mb-2">Project Type</label>
                              <Input placeholder="Select project type" className="bg-white/10 border-white/20" />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-foreground mb-2">Start Date</label>
                              <Input type="date" className="bg-white/10 border-white/20" />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-foreground mb-2">Expected Duration</label>
                              <Input placeholder="e.g., 3 months" className="bg-white/10 border-white/20" />
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <Card className="bg-white/5 border-white/20">
                        <CardHeader>
                          <CardTitle className="text-xl flex items-center gap-2">
                            <Calendar className="h-5 w-5" />
                            Milestone Planning
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="space-y-3">
                            <div className="p-3 rounded-lg border border-white/10 bg-white/5">
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-sm">Phase 1: Initial Setup</span>
                                <Badge variant="outline" className="text-xs">Week 1-2</Badge>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">Configure basic project structure and team access</p>
                            </div>
                            <div className="p-3 rounded-lg border border-white/10 bg-white/5">
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-sm">Phase 2: Content Migration</span>
                                <Badge variant="outline" className="text-xs">Week 3-4</Badge>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">Import existing documents and establish workflows</p>
                            </div>
                            <div className="p-3 rounded-lg border border-white/10 bg-white/5">
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-sm">Phase 3: Go Live</span>
                                <Badge variant="outline" className="text-xs">Week 5-6</Badge>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">Final testing and official project launch</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </TabsContent>

                    {/* Notes Tab */}
                    <TabsContent value="notes" className="mt-0 h-full space-y-6">
                      <Card className="bg-white/5 border-white/20">
                        <CardHeader>
                          <CardTitle className="text-xl flex items-center gap-2">
                            <FileText className="h-5 w-5" />
                            Project Notes
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-foreground mb-2">General Notes</label>
                            <Textarea 
                              placeholder="Add any important project notes, reminders, or observations here..."
                              className="min-h-[150px] bg-white/10 border-white/20"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-foreground mb-2">Requirements & Constraints</label>
                            <Textarea 
                              placeholder="Document any specific requirements, constraints, or special considerations for this project..."
                              className="min-h-[120px] bg-white/10 border-white/20"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-foreground mb-2">Next Steps</label>
                            <Textarea 
                              placeholder="Outline immediate next steps and action items..."
                              className="min-h-[100px] bg-white/10 border-white/20"
                            />
                          </div>
                        </CardContent>
                      </Card>
                    </TabsContent>
                  </div>
                </div>
              </Tabs>
            </div>

            {/* Footer */}
            <div className="border-t border-white/10 px-6 py-4 bg-linear-to-t from-primary/5 to-transparent flex justify-between items-center">
              <div className="text-sm text-muted-foreground">
                <span className="text-blue-600">Project setup in progress</span>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setOpen(false)}>Save Draft</Button>
                <Button>Continue Setup</Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
